/**
 * SVG path generators for all chart types.
 * Pure functions that convert data points and scales into SVG paths and element attributes.
 */

import { getMedianInterval, getPointValue } from "./dataUtils";
import type { OhlcvPoint, PricePoint, Scale } from "~types/types";

// ==================== Types ====================

export interface PathGeneratorOptions {
  xScale: Scale;
  yScale: Scale;
  /** Break path on detected gaps (default: false for continuous line) */
  breakOnGaps?: boolean;
}

type CandleDirection = "up" | "down" | "neutral";

export interface CandlestickRect {
  readonly timestamp: number;
  x: number;
  y: number;
  width: number;
  height: number;
  wickTop: number;
  wickBottom: number;
  direction: CandleDirection;
}

export interface CandlestickGeneratorOptions extends PathGeneratorOptions {
  candleWidth?: number;
  spacing?: number;
}

export interface VolumeBar {
  readonly timestamp: number;
  x: number;
  y: number;
  width: number;
  height: number;
  direction: CandleDirection;
}

export interface VolumeGeneratorOptions {
  xScale: Scale;
  volumeScale: Scale;
  baselineY: number;
  barWidth?: number;
  spacing?: number;
  priceData?: (PricePoint | OhlcvPoint)[];
}

export interface CrosshairPaths {
  vertical: string;
  horizontal: string;
}

export interface CrosshairOptions {
  x: number;
  y: number;
  chartArea: { left: number; top: number; right: number; bottom: number };
}

export interface GridLines {
  horizontal: string[];
  vertical: string[];
}

export interface GridOptions {
  xTicks: number[];
  yTicks: number[];
  chartArea: { left: number; top: number; right: number; bottom: number };
}

// ==================== Helper Functions ====================

/**
 * Check if data point is valid (not NaN, undefined, null).
 */
export function isValidDataPoint(point: PricePoint | OhlcvPoint): boolean {
  if (
    !point ||
    typeof point.timestamp !== "number" ||
    !Number.isFinite(point.timestamp)
  ) {
    return false;
  }

  if ("value" in point) {
    return typeof point.value === "number" && Number.isFinite(point.value);
  }

  if ("close" in point) {
    return (
      typeof point.open === "number" &&
      Number.isFinite(point.open) &&
      typeof point.high === "number" &&
      Number.isFinite(point.high) &&
      typeof point.low === "number" &&
      Number.isFinite(point.low) &&
      typeof point.close === "number" &&
      Number.isFinite(point.close)
    );
  }

  return false;
}

/**
 * Detect if there's a gap in data (for breaking line path).
 * Gap is defined as timestamp difference > 2x the median interval.
 */
export function hasDataGap(
  prevPoint: PricePoint | OhlcvPoint,
  currPoint: PricePoint | OhlcvPoint,
  medianInterval: number
): boolean {
  if (medianInterval === 0) return false;

  return currPoint.timestamp - prevPoint.timestamp > medianInterval * 2;
}

/**
 * Round to 2 decimal places for smaller SVG size.
 */
function round(value: number): number {
  return Math.round(value * 100) / 100;
}

// ==================== Line Path Generator ====================

/**
 * Generate SVG path `d` attribute for a line chart.
 * Uses PricePoint.value or OhlcvPoint.close for y-values.
 * By default draws a continuous line. Set breakOnGaps=true to break path at gaps.
 */
export function generateLinePath(
  data: (PricePoint | OhlcvPoint)[],
  options: PathGeneratorOptions
): string {
  const { xScale, yScale, breakOnGaps = false } = options;

  if (data.length === 0) return "";

  // Only calculate median interval if we need to detect gaps
  const medianInterval = breakOnGaps ? getMedianInterval(data) ?? 0 : 0;
  const pathSegments: string[] = [];
  let currentPath = "";
  let isFirst = true;
  let prevPoint: (PricePoint | OhlcvPoint) | null = null;

  for (const point of data) {
    if (!isValidDataPoint(point)) continue;

    const x = round(xScale.scale(point.timestamp));
    const y = round(yScale.scale(getPointValue(point)));

    if (
      breakOnGaps &&
      prevPoint &&
      hasDataGap(prevPoint, point, medianInterval)
    ) {
      if (currentPath) pathSegments.push(currentPath);
      currentPath = `M ${x},${y}`;
      isFirst = false;
    } else if (isFirst) {
      currentPath = `M ${x},${y}`;
      isFirst = false;
    } else {
      currentPath += ` L ${x},${y}`;
    }

    prevPoint = point;
  }

  if (currentPath) pathSegments.push(currentPath);

  return pathSegments.join(" ");
}

// ==================== Area Path Generator ====================

/**
 * Generate SVG path for area fill below line.
 * Closes path to bottom of chart area.
 * By default draws a continuous area. Set breakOnGaps=true to break at gaps.
 */
export function generateAreaPath(
  data: (PricePoint | OhlcvPoint)[],
  options: PathGeneratorOptions & { baselineY: number }
): string {
  const { xScale, yScale, baselineY, breakOnGaps = false } = options;

  if (data.length === 0) return "";

  // Only calculate median interval if we need to detect gaps
  const medianInterval = breakOnGaps ? getMedianInterval(data) ?? 0 : 0;
  const pathSegments: string[] = [];
  let currentPath = "";
  let startX = 0;
  let isFirst = true;
  let prevPoint: (PricePoint | OhlcvPoint) | null = null;

  for (const point of data) {
    if (!isValidDataPoint(point)) continue;

    const x = round(xScale.scale(point.timestamp));
    const y = round(yScale.scale(getPointValue(point)));

    if (
      breakOnGaps &&
      prevPoint &&
      hasDataGap(prevPoint, point, medianInterval)
    ) {
      if (currentPath) {
        const prevX = round(xScale.scale(prevPoint.timestamp));

        currentPath += ` L ${prevX},${baselineY} L ${startX},${baselineY} Z`;
        pathSegments.push(currentPath);
      }
      currentPath = `M ${x},${y}`;
      startX = x;
      isFirst = false;
    } else if (isFirst) {
      currentPath = `M ${x},${y}`;
      startX = x;
      isFirst = false;
    } else {
      currentPath += ` L ${x},${y}`;
    }

    prevPoint = point;
  }

  if (currentPath && prevPoint) {
    const lastX = round(xScale.scale(prevPoint.timestamp));

    currentPath += ` L ${lastX},${baselineY} L ${startX},${baselineY} Z`;
    pathSegments.push(currentPath);
  }

  return pathSegments.join(" ");
}

// ==================== Candlestick Generator ====================

/**
 * Calculate optimal candle width based on data density and chart width.
 */
export function calculateCandleWidth(
  dataPointCount: number,
  chartWidth: number,
  minWidth = 1,
  maxWidth = 20
): number {
  if (dataPointCount === 0) return minWidth;

  const availableWidth = chartWidth / dataPointCount;
  const width = Math.max(minWidth, Math.min(maxWidth, availableWidth * 0.8));

  return Math.floor(width);
}

/**
 * Generate candlestick rectangles and wicks.
 */
export function generateCandlesticks(
  data: OhlcvPoint[],
  options: CandlestickGeneratorOptions
): CandlestickRect[] {
  const { xScale, yScale, spacing = 1 } = options;

  const candleWidth =
    options.candleWidth ??
    calculateCandleWidth(data.length, xScale.range()[1] - xScale.range()[0]);

  return data.map((point) => {
    const x = xScale.scale(point.timestamp);
    const openY = yScale.scale(point.open);
    const closeY = yScale.scale(point.close);
    const highY = yScale.scale(point.high);
    const lowY = yScale.scale(point.low);

    let direction: CandleDirection = "neutral";

    if (point.close > point.open) {
      direction = "up";
    } else if (point.close < point.open) {
      direction = "down";
    }

    return {
      timestamp: point.timestamp,
      x: round(x - candleWidth / 2),
      y: round(Math.min(openY, closeY)),
      width: candleWidth - spacing,
      height: Math.max(1, round(Math.abs(closeY - openY))),
      wickTop: round(highY),
      wickBottom: round(lowY),
      direction,
    };
  });
}

function getBarDirection(
  priceData: (PricePoint | OhlcvPoint)[] | undefined,
  index: number
): CandleDirection {
  if (!priceData) return "neutral";

  const pricePoint = priceData[index];

  if (!pricePoint) return "neutral";

  if ("close" in pricePoint && "open" in pricePoint) {
    return pricePoint.close >= pricePoint.open ? "up" : "down";
  }

  if ("value" in pricePoint && index > 0) {
    const prevPoint = priceData[index - 1];

    if (prevPoint) {
      return pricePoint.value >= getPointValue(prevPoint) ? "up" : "down";
    }
  }

  return "neutral";
}

/**
 * Generate volume bar rectangles.
 */
export function generateVolumeBars(
  data: (PricePoint | OhlcvPoint)[],
  options: VolumeGeneratorOptions
): VolumeBar[] {
  const { xScale, volumeScale, baselineY, priceData, spacing = 1 } = options;

  const barWidth =
    options.barWidth ??
    calculateCandleWidth(data.length, xScale.range()[1] - xScale.range()[0]);

  return data.map((point, index) => {
    const x = xScale.scale(point.timestamp);
    const volume = point.volume ?? 0;
    const volumeY = volumeScale.scale(volume);
    const direction = getBarDirection(priceData, index);

    return {
      timestamp: point.timestamp,
      x: round(x - barWidth / 2),
      y: round(Math.min(volumeY, baselineY)),
      width: Math.max(1, barWidth - spacing),
      height: round(Math.abs(baselineY - volumeY)),
      direction,
    };
  });
}

// ==================== Crosshair Generator ====================

/**
 * Generate crosshair paths constrained to chart area.
 */
export function generateCrosshairPaths(
  options: CrosshairOptions
): CrosshairPaths {
  const { x, y, chartArea } = options;

  const vertical = `M ${x},${chartArea.top} L ${x},${chartArea.bottom}`;
  const horizontal = `M ${chartArea.left},${y} L ${chartArea.right},${y}`;

  return { vertical, horizontal };
}

// ==================== Grid Line Generator ====================

/**
 * Generate grid line paths.
 */
export function generateGridLines(options: GridOptions): GridLines {
  const { xTicks, yTicks, chartArea } = options;

  const horizontal = yTicks.map((y) => {
    return `M ${chartArea.left},${round(y)} L ${chartArea.right},${round(y)}`;
  });

  const vertical = xTicks.map((x) => {
    return `M ${round(x)},${chartArea.top} L ${round(x)},${chartArea.bottom}`;
  });

  return { horizontal, vertical };
}

// ==================== Segmented Path Generator ====================

export interface SegmentedPaths {
  normalPath: string;
  filledPath: string;
}

/**
 * Generate separate SVG paths for filled and non-filled segments.
 * Filled segments (gap-filled data points) will be rendered with dashed lines.
 *
 * Uses ProcessedPricePoint.isFilled to distinguish between:
 * - Normal segments: Original data points (solid line)
 * - Filled segments: Gap-filled points (dashed line)
 */
function startNewSegment(
  prevPoint: { timestamp: number; value: number } | undefined,
  x: string,
  y: string,
  xScale: Scale,
  yScale: Scale
): string {
  if (!prevPoint) return `M ${x},${y}`;

  const prevX = xScale.scale(prevPoint.timestamp).toFixed(2);
  const prevY = yScale.scale(prevPoint.value).toFixed(2);

  return `M ${prevX},${prevY} L ${x},${y}`;
}

function commitSegment(
  path: string,
  isFilled: boolean,
  normalSegments: string[],
  filledSegments: string[]
): void {
  if (isFilled) {
    filledSegments.push(path);
  } else {
    normalSegments.push(path);
  }
}

export function generateSegmentedPaths(
  data: Array<{ timestamp: number; value: number; isFilled?: boolean }>,
  xScale: Scale,
  yScale: Scale
): SegmentedPaths {
  if (data.length === 0) return { normalPath: "", filledPath: "" };

  const normalSegments: string[] = [];
  const filledSegments: string[] = [];

  let currentPath = "";
  let currentIsFilled = data[0]?.isFilled ?? false;

  for (let i = 0; i < data.length; i++) {
    const point = data[i];

    if (!point) continue;

    const x = xScale.scale(point.timestamp).toFixed(2);
    const y = yScale.scale(point.value).toFixed(2);
    const isFilled = point.isFilled ?? false;

    if (i === 0) {
      currentPath = `M ${x},${y}`;
      currentIsFilled = isFilled;
    } else if (isFilled === currentIsFilled) {
      currentPath += ` L ${x},${y}`;
    } else {
      commitSegment(
        currentPath,
        currentIsFilled,
        normalSegments,
        filledSegments
      );
      currentPath = startNewSegment(data[i - 1], x, y, xScale, yScale);
      currentIsFilled = isFilled;
    }
  }

  if (currentPath) {
    commitSegment(currentPath, currentIsFilled, normalSegments, filledSegments);
  }

  return {
    normalPath: normalSegments.join(" "),
    filledPath: filledSegments.join(" "),
  };
}
